import React from 'react';

/**
 * WorkspaceLayout - Main layout wrapper for all workspaces
 * Ensures fixed header, sticky columns, and scrollable grid body
 */
export const WorkspaceLayout = ({ children, theme = 'light', className = '' }) => {
  const isDark = theme === 'dark';
  
  return (
    <div 
      className={`h-full w-full flex flex-col overflow-hidden ${isDark ? 'bg-[#0a0a0a]' : 'bg-gray-50'} ${className}`}
      data-testid="workspace-layout"
    >
      {children}
    </div>
  );
};

/**
 * WorkspaceHeader - Fixed header for workspace screens
 */
export const WorkspaceHeader = ({ children, theme = 'light', className = '' }) => {
  const isDark = theme === 'dark';
  
  return (
    <div 
      className={`shrink-0 border-b ${isDark ? 'border-[#222] bg-[#0e0e10]' : 'border-gray-200 bg-white'} ${className}`}
      data-testid="workspace-header"
    >
      {children}
    </div>
  );
};

/**
 * WorkspaceContent - Scrollable content area
 */
export const WorkspaceContent = ({ children, theme = 'light', className = '' }) => {
  return (
    <div 
      className={`flex-1 overflow-hidden flex flex-col ${className}`}
      data-testid="workspace-content"
    >
      {children}
    </div>
  );
};

/**
 * WorkspaceToolbar - Toolbar for filters and actions
 */
export const WorkspaceToolbar = ({ children, theme = 'light', className = '' }) => {
  const isDark = theme === 'dark';
  
  return (
    <div 
      className={`shrink-0 px-4 py-3 border-b flex flex-wrap items-center gap-3 ${isDark ? 'border-[#222] bg-[#0e0e10]' : 'border-gray-100 bg-white'} ${className}`}
      data-testid="workspace-toolbar"
    >
      {children}
    </div>
  );
};

/**
 * WorkspaceSummary - Summary cards section
 */
export const WorkspaceSummary = ({ children, theme = 'light', className = '' }) => {
  return (
    <div 
      className={`shrink-0 px-4 py-3 grid gap-3 ${className}`}
      style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}
      data-testid="workspace-summary"
    >
      {children}
    </div>
  );
};

/**
 * SummaryCard - Individual summary card
 */
export const SummaryCard = ({ 
  label, 
  value, 
  suffix = '', 
  theme = 'light', 
  variant = 'default',
  icon = null 
}) => {
  const isDark = theme === 'dark';
  
  const variants = {
    default: isDark ? 'text-white' : 'text-gray-900',
    success: 'text-green-500',
    danger: 'text-red-500',
    warning: 'text-amber-500',
    info: 'text-blue-500'
  };
  
  return (
    <div className={`p-4 rounded-xl border ${isDark ? 'bg-[#111] border-[#222]' : 'bg-white border-gray-200'}`}>
      <div className="flex items-center justify-between">
        <p className={`text-xs font-medium ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>{label}</p>
        {icon && <div className={isDark ? 'text-slate-600' : 'text-gray-400'}>{icon}</div>}
      </div>
      <p className={`text-xl font-bold mt-1 ${variants[variant]}`}>
        {value}{suffix && <span className="text-sm font-normal ml-1">{suffix}</span>}
      </p>
    </div>
  );
};

export default {
  WorkspaceLayout,
  WorkspaceHeader,
  WorkspaceContent,
  WorkspaceToolbar,
  WorkspaceSummary,
  SummaryCard
};
